import type { DateRangeValue } from "./DateRangeField";
import type { EventType } from "./eventTypes";
import type { PickedMember } from "./MemberPicker";
import { defaultGranularity, endOfDay, Granularity, PRESET_DAYS, presetRange, RangePreset, startOfDay } from "./reportFormat";
import type { ComparePreset } from "./scorecardFormat";

/**
 * What the Reports page knows without a request: its reports, and the
 * parameters each one takes. Free of React so the rules can be tested alone.
 */

export type ReportKey =
  | "scorecard"
  | "daily"
  | "weekly"
  | "monthly"
  | "engagement"
  | "retention"
  | "events"
  | "screens"
  | "otp"
  | "totals";

export type ResultView = "chart" | "table";

export interface ReportDefinition {
  key: ReportKey;
  label: string;
  /** Shown under the report's name in the parameter pane. */
  description: string;
  /** Set for a report that can show its results both ways: the view it opens on. */
  defaultView?: ResultView;
}

/** A report that keeps its own screen elsewhere in the dashboard. Its entry goes to it. */
export interface ReportLink {
  label: string;
  to: string;
}

export type ReportMenuItem = ReportDefinition | ReportLink;

export interface ReportGroup {
  title: string;
  items: ReportMenuItem[];
}

export const isReportLink = (item: ReportMenuItem): item is ReportLink => "to" in item;

/**
 * The menu, by the business area each report serves, in order. `tab` in the URL
 * holds a report's key, so older links still open it wherever it is filed.
 */
export const REPORT_GROUPS: ReportGroup[] = [
  {
    title: "Members",
    items: [
      {
        key: "scorecard",
        label: "Scorecard",
        description: "The numbers that say whether members stay, against an earlier stretch of days.",
      },
      {
        key: "engagement",
        label: "Engagement",
        description: "Who was active, how long they stayed, and what they did.",
        defaultView: "chart",
      },
      {
        key: "retention",
        label: "Retention",
        description: "Of the members who registered, how many came back on days 1, 7 and 30.",
        defaultView: "table",
      },
      {
        key: "events",
        label: "Events",
        description: "Every registration, app open, referral and recommendation, newest first.",
        defaultView: "table",
      },
      {
        key: "screens",
        label: "Screens",
        description: "Which screens members visit and how often.",
        defaultView: "chart",
      },
      { label: "User Activity", to: "/user-activity" },
      { label: "Point History", to: "/point-history" },
      { label: "Shared Referral Links", to: "/referrals" },
    ],
  },
  {
    title: "Businesses",
    items: [
      {
        key: "totals",
        label: "Totals by region",
        description: "The original counts, split by the US, the DMV, Zimbabwe and one region you pick.",
      },
      { label: "Business Recommendation Activity", to: "/business-recommendation-activity" },
    ],
  },
  {
    title: "Admin",
    items: [
      {
        key: "otp",
        label: "OTP failures",
        description: "Why sign-in codes were not sent, explained and counted.",
      },
    ],
  },
  {
    title: "Periodic",
    items: [
      {
        key: "daily",
        label: "Daily report",
        description: "The team's daily report: one table per section, a column per day.",
      },
      {
        key: "weekly",
        label: "Weekly report",
        description: "The daily report's tables over one Monday-to-Sunday week, with the week's total.",
      },
      {
        key: "monthly",
        label: "Monthly report",
        description: "The daily report's tables over one calendar month, with the month's total.",
      },
    ],
  },
];

export const REPORTS: ReportDefinition[] = REPORT_GROUPS.flatMap((group) =>
  group.items.filter((item): item is ReportDefinition => !isReportLink(item)),
);

/** The menu narrowed to reports whose name holds what was typed. A group with none left is dropped. */
export function searchReportGroups(query: string, groups: ReportGroup[] = REPORT_GROUPS): ReportGroup[] {
  const wanted = query.trim().toLowerCase();
  if (!wanted) return groups;
  return groups
    .map((group) => ({ ...group, items: group.items.filter((item) => item.label.toLowerCase().includes(wanted)) }))
    .filter((group) => group.items.length > 0);
}

export const reportOf = (key: string | null): ReportDefinition => REPORTS.find((report) => report.key === key) ?? REPORTS[0];

export type RegionOption = { name: string; displayName: string } | null;

export interface Region {
  country: RegionOption;
  state: RegionOption;
  city: RegionOption;
}

export const NO_REGION: Region = { country: null, state: null, city: null };

export interface SharedFilters extends Region {
  preset: RangePreset;
  from: Date;
  to: Date;
  granularity: Granularity;
}

export type OtpChannel = "all" | "sms" | "email";

/**
 * Every parameter of every report. The dates, split and region are shared, so a
 * range chosen for one report is still there on the next; the rest belong to
 * one report and wait for it.
 */
export interface ReportParams {
  filters: SharedFilters;
  daily: DateRangeValue;
  weekly: DateRangeValue;
  monthly: DateRangeValue;
  retentionCohort: Granularity;
  events: { types: EventType[]; member: PickedMember | null };
  screens: { member: PickedMember | null };
  otp: { channel: OtpChannel; destination: string };
  scorecard: { preset: ComparePreset; custom: { current: DateRangeValue; previous: DateRangeValue } };
  totals: { dates: DateRangeValue; people: Region; places: Region };
}

/** The last complete week, Monday to Sunday, as the other reports' weeks run. */
export function lastFullWeek(now: Date): DateRangeValue {
  const sinceMonday = (now.getDay() + 6) % 7;
  const from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - sinceMonday - 7);
  const to = new Date(from.getFullYear(), from.getMonth(), from.getDate() + 6);
  return { preset: "custom", from: startOfDay(from), to: endOfDay(to) };
}

/** The last complete calendar month. */
export function lastFullMonth(now: Date): DateRangeValue {
  const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const to = new Date(now.getFullYear(), now.getMonth(), 0);
  return { preset: "custom", from: startOfDay(from), to: endOfDay(to) };
}

/** The last 28 complete days and the 28 before, as the scorecard's presets have them. */
function initialCustomCompare(now: Date): ReportParams["scorecard"]["custom"] {
  const day = 24 * 60 * 60 * 1000;
  const current = presetRange(28, new Date(now.getTime() - day));
  const previous = presetRange(28, new Date(current.from.getTime() - day));
  return { current: { preset: "custom", ...current }, previous: { preset: "custom", ...previous } };
}

export function initialReportParams(now: Date = new Date()): ReportParams {
  const { from, to } = presetRange(PRESET_DAYS["30d"], now);
  return {
    filters: { preset: "30d", from, to, granularity: defaultGranularity(from, to), ...NO_REGION },
    daily: { preset: "7d", ...presetRange(7, now) },
    weekly: lastFullWeek(now),
    monthly: lastFullMonth(now),
    retentionCohort: "WEEK",
    events: { types: [], member: null },
    screens: { member: null },
    otp: { channel: "all", destination: "" },
    scorecard: { preset: "28", custom: initialCustomCompare(now) },
    totals: { dates: { preset: "all", from: null, to: null }, people: NO_REGION, places: NO_REGION },
  };
}

const range = ({ preset, from, to }: SharedFilters) => ({ preset, from, to });
const region = ({ country, state, city }: Region) => ({ country, state, city });

/** Only what this report reads, so a change made for another report does not mark it out of date. */
export function paramsFor(key: ReportKey, params: ReportParams): unknown {
  const { filters } = params;
  switch (key) {
    case "scorecard":
      return { compare: params.scorecard, region: region(filters) };
    case "daily":
      return params.daily;
    case "weekly":
      return params.weekly;
    case "monthly":
      return params.monthly;
    case "engagement":
      return filters;
    case "retention":
      return { range: range(filters), region: region(filters), cohort: params.retentionCohort };
    case "events":
      return { range: range(filters), region: region(filters), events: params.events };
    case "screens":
      return { range: range(filters), region: region(filters), screens: params.screens };
    case "otp":
      return { range: range(filters), granularity: filters.granularity, otp: params.otp };
    case "totals":
      return params.totals;
  }
}

/** True when the pane no longer says what the results on screen were run with. */
export const paramsChanged = (key: ReportKey, shown: ReportParams, ran: ReportParams): boolean =>
  JSON.stringify(paramsFor(key, shown)) !== JSON.stringify(paramsFor(key, ran));

const refreshRange = <T extends { preset: string; from: Date | null; to: Date | null }>(value: T, now: Date): T => {
  const days = PRESET_DAYS[value.preset as keyof typeof PRESET_DAYS];
  return days ? { ...value, ...presetRange(days, now) } : value;
};

/**
 * "Last 30 days" means the 30 days before this Run, not before the moment it
 * was picked, so a page left open overnight does not re-run yesterday's window.
 * A custom range is left exactly as chosen.
 */
export function withFreshDates(params: ReportParams, now: Date = new Date()): ReportParams {
  return {
    ...params,
    filters: refreshRange(params.filters, now),
    daily: refreshRange(params.daily, now),
    totals: { ...params.totals, dates: refreshRange(params.totals.dates, now) },
  };
}

const day = (date: Date | null) =>
  date ? date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";

const placeOf = (place: Region) => place.city?.name || place.state?.name || place.country?.name || "";

const SPLIT_WORDS: Record<Granularity, string> = { DAY: "by day", WEEK: "by week", MONTH: "by month" };

/** One line saying what the results on screen were run with, whatever the pane now holds. */
export function describeRun(key: ReportKey, params: ReportParams): string {
  const { filters } = params;
  const dates = `${day(filters.from)} to ${day(filters.to)}`;
  const parts: string[] = [];
  switch (key) {
    case "scorecard":
      parts.push(placeOf(filters) || "All regions");
      break;
    case "daily":
      parts.push(`${day(params.daily.from)} to ${day(params.daily.to)}`);
      break;
    case "weekly":
      parts.push(`${day(params.weekly.from)} to ${day(params.weekly.to)}`);
      break;
    case "monthly":
      parts.push(`${day(params.monthly.from)} to ${day(params.monthly.to)}`);
      break;
    case "engagement":
      parts.push(dates, SPLIT_WORDS[filters.granularity], placeOf(filters));
      break;
    case "retention":
      parts.push(dates, SPLIT_WORDS[params.retentionCohort], placeOf(filters));
      break;
    case "events":
      parts.push(dates, placeOf(filters), params.events.member ? params.events.member.username : "");
      break;
    case "screens":
      parts.push(dates, placeOf(filters), params.screens.member ? params.screens.member.username : "");
      break;
    case "otp":
      parts.push(dates, SPLIT_WORDS[filters.granularity], params.otp.destination);
      break;
    case "totals":
      parts.push(params.totals.dates.from ? `${day(params.totals.dates.from)} to ${day(params.totals.dates.to)}` : "All time");
      break;
  }
  return parts.filter(Boolean).join(" · ");
}
